import { Component, OnInit } from '@angular/core';
import { EntityService } from '../../services/entity.service';
import Lottie from 'lottie-web';
import { EntityInterface } from '../../models/entity.interface';
import { Constants } from '../../models/constants.class';
import { Router } from '@angular/router';
import {
  AggregatioRequestInterface,
  AggregationResponseInterface,
} from '@app/models/aggregation.interface';
import { Psd2Service } from '@app/services/psd2.service';
import { TealiumService } from '@app/services/tealium.service';
import { TranslateService } from '@ngx-translate/core';
import { ConfigService } from '@ng-darwin/config';
import { popupWindow } from '@app/helpers/utils';
import { EntityProductType } from '@app/models/entity.definitions';

export interface IEntitiesState {
  index: number;
  step: number;
  entitiesLength: number;
  entities: EntityInterface[];
  retry?: string | null;
  retryId?: string | null;
  retryIndex?: string | null;
  currentAggregatedEntities?: EntityInterface[];
}

@Component({
  standalone: false,
  selector: 'app-ag-entities-loader',
  templateUrl: './ag-entities-loader.component.html',
  styleUrls: ['./ag-entities-loader.component.scss'],
})
export class AgEntitiesLoaderComponent implements OnInit {
  public bankName = '';
  public step = 1;
  public entities: EntityInterface[] = [];
  public counterText = '';
  public isPopupsModalOpened = false;
  public constants = Constants;
  public entitiesLength = 0;
  public index = 0;
  public isWebview = false;
  public emergentWindow: any; // Ventana emergente
  public windowState = 0; // Estado de la ventana
  private _consentCodeReceived = false;
  public entitiesState: IEntitiesState | undefined;
  window: any = window;

  messageHandler: any;

  /**
   * @description Manejador para el evento message
   * @param event MessageEvent
   */
  public readonly windowEventListener: (event: MessageEvent) => Promise<void> =
    async (event: MessageEvent) => {
      this.entities = this._entityService.getSelectedEntities();
      const code = event.data.code || '';
      if (
        event.data.event_id === Constants.windowEvents.loginId &&
        code !== ''
      ) {
        const retryIndex = JSON.parse(sessionStorage.getItem('retryIndex')!);
        const id = JSON.parse(sessionStorage.getItem('retryId')!);
        if (retryIndex?.toString() && this.entities[retryIndex].id === id) {
          this.index = retryIndex;
        }
        this.entities[this.index].consentCode = code;
        this._consentCodeReceived = true;
        this.checkForNavigationToEntitiesProgress();
      }
    };

  constructor(
    private _entityService: EntityService,
    private _router: Router,
    private readonly _psd2Service: Psd2Service,
    private readonly _tealiumService: TealiumService,
    private readonly _translateService: TranslateService,
    private readonly _configService: ConfigService,
  ) {
    this.messageHandler = this.windowEventListener.bind(this);
  }

  ngOnInit(): void {
    this.loadAnimatedLogo();
    this.windowEvent();
    this.checkForQueryParamsState();
    this.checkIsWebview();

    console.warn('[Entities State] Is Webview', this.isWebview);
    console.warn('[Entities State] State', this.entitiesState);
    if (this.isWebview && this.entitiesState) {
      this.checkForSelectedEntities();
      this.checkForNavigationToEntitiesProgress();
      return;
    }

    this.entities = this._entityService.getSelectedEntities();

    this.isRetry();

    this.entitiesLength = this.entities.length;
    this.entities.forEach((entity) => {
      if (this.step === 1 && entity.type === Constants.entityPSD2Type.a1) {
        this._tealiumService.sendPageLoad({
          page_name: '/PSD2/agregar_banco/permitir_conexion/A1',
          page_title: 'PSD2 agregar banco permitir conexion A1',
          page_type: 'detalle',
          page_category: 'servicing',
          product_scope: 'operativa_no_financiera',
          product_category: 'PSD2',
          product_name: 'crédito digital',
          added_bank: entity.name,
        });
      } else {
        this._tealiumService.sendPageLoad({
          page_name: '/PSD2/agregar_banco/anadir_cuenta' + `/${entity.type}`,
          page_title: 'PSD2 agregar banco añadir cuenta ' + entity.type,
          page_type: 'detalle',
          page_category: 'servicing',
          product_scope: 'operativa_no_financiera',
          product_category: 'PSD2',
          product_name: 'crédito digital',
          added_bank: entity.name,
        });
      }
    });
    this.incrementStep();
  }

  incrementStep(): void {
    this.step++;
    this.updateEntitiesState();
    this.initEntitiesAggregation(this.index);
  }

  async initEntitiesAggregation(i: number): Promise<void> {
    if (
      (this.step === 2 &&
        this.entities[i].type === Constants.entityPSD2Type.a2) ||
      (this.step === 3 && this.entities[i].type === Constants.entityPSD2Type.a1)
    ) {
      await this.aggregationPsd2(this.entities[i]);
    }
  }

  aggregationPsd2(entity: EntityInterface): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      this.bankName = entity.name as string;
      this.counterText =
        this.index + 1 + ' de ' + this.entities.length + ' bancos';
      const aggregationRequest: AggregatioRequestInterface = {};
      let aggregationResponse: AggregationResponseInterface = {};
      const product = entity.products || [];
      aggregationRequest.aspspcode = entity.code;
      aggregationRequest.products = product
        .filter((value) => value.type === Constants.entityType.psd2)
        .map((value) => value.name || '');
      aggregationRequest.params = JSON.parse(
        sessionStorage.getItem(Constants.url.params) as string,
      );
      aggregationRequest.params?.push({
        name: Constants.language,
        value: this._translateService.getDefaultLang(),
      });

      this.addOrUpdateEntitiesStateParam(aggregationRequest.params);

      if (
        sessionStorage.getItem('app-unique-id') ===
          this._configService.config.app['cdncApplication'] &&
        this.isWebview
      ) {
        aggregationRequest.params?.push({
          name: 'token',
          value: sessionStorage.getItem('_dwtk') as string,
        });
      }
      // Recuperar el expediente de la sessionStorage y añadirlo al aggregationRequest.expedientCode
      aggregationRequest.expedientCode =
        sessionStorage.getItem('prop-unique-id') || '';

      this._psd2Service
        .postAggregation(aggregationRequest)
        .then((response) => {
          aggregationResponse = response || {};
          // se debe mostrar el mensaje que sea definido
          this._psd2Service.setConsentCode(Constants.consentOk);
          resolve();
        })
        .catch((errorResponse) => {
          if (this.isWebview) {
            setTimeout(() => {
              this.manageErrorResponse(errorResponse, aggregationResponse)
                .then(() => this.awaitForEntityConsentCode())
                .catch(() => resolve());
            }, 6000);
            return;
          }

          this.manageErrorResponse(errorResponse, aggregationResponse)
            .then(() => this.awaitForEntityConsentCode())
            .catch(() => resolve());
        });
    });
  }

  manageErrorResponse(
    errorResponse: any,
    aggregationResponse: AggregationResponseInterface,
  ): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      aggregationResponse = errorResponse.error;
      switch (errorResponse.status) {
        case 401:
          this.handle401Error(aggregationResponse, resolve, reject);
          break;
        case 403:
          if (aggregationResponse.consentURL) {
            this._psd2Service.setConsentCode('consentNoOk');
            resolve();
          } else {
            this._tealiumService.sendError({
              category: 'Consent error',
              action: 'warning',
              data: {
                cod_error: 403,
                desc_error: 'popup window',
              },
            });
            this.step = -1;
            reject();
          }
          break;
        default:
          this._tealiumService.sendError({
            category: 'Server error',
            action: 'error',
            data: {
              cod_error: 500,
              desc_error: 'popup window',
            },
          });
          this.step = -1;
          reject();
          break;
      }
    });
  }

  private handle401Error(
    aggregationResponse: AggregationResponseInterface,
    resolve: () => void,
    reject: () => void,
  ): void {
    if (aggregationResponse.authorizationURL) {
      if (this.isWebview) {
        this.updateEntitiesState();
        sessionStorage.setItem('entitiesState', this.getBASE64EntitiesState());
      }
      this.emergentWindow = popupWindow(
        aggregationResponse.authorizationURL || '',
        true,
      );
      try {
        this.emergentWindow.focus();
        this.windowState = 1;
        this.windowCheckInterval().then(() => resolve());
      } catch (e) {
        if (!this.isWebview) {
          this.windowState = 2;
          this._tealiumService.sendError({
            category: 'Popup windows are blocked',
            action: 'warning',
            data: {
              cod_error: 401,
              desc_error: 'popup window',
            },
          });
          this.isPopupsModalOpened = true;
          reject();
        }
      }
    } else {
      this._tealiumService.sendError({
        category: 'Server error',
        action: 'warning',
        data: {
          cod_error: 401,
          desc_error: 'popup window',
        },
      });
      this.step = -1;
      resolve();
    }
  }

  windowEvent(): void {
    if (this.window.trussoEventListener) {
      window.removeEventListener('message', this.messageHandler);
      this.window.trussoEventListener = false;
      console.warn('Removing event listener');
    }

    window.addEventListener('message', this.messageHandler);
    this.window.trussoEventListener = true;
  }

  windowCheckInterval(): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      const interval = setInterval(() => {
        this.checkWin().then(() => {
          clearInterval(interval);
          resolve();
        });
      }, 1000);
    });
  }

  checkWin(): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      if (!this.emergentWindow) {
        this.windowState = 0; // Estado ventana 0
      } else {
        if (this.emergentWindow.closed) {
          this.windowState = 2; // Estado ventana 2, cerrada
          resolve();
        } else {
          this.windowState = 1; // Estado ventana 1, abierta
        }
      }
    });
  }

  loadAnimatedLogo(): void {
    const svgContainer: any = document.getElementById('logo-animated');
    Lottie.loadAnimation({
      container: svgContainer,
      renderer: 'svg',
      loop: true,
      autoplay: true,
      path: './assets/images/animated/animated_logo_light.json',
    });
  }

  ngOnDestroy(): void {
    sessionStorage.removeItem('retry');
    sessionStorage.removeItem('retryId');
    sessionStorage.removeItem('retryIndex');
    window.removeEventListener('message', this.messageHandler);
  }

  /**
   * Await for consentCode passed in windowEventListener, if no consentCode received, this method
   * should call incrementStep.
   */
  awaitForEntityConsentCode(): void {
    setTimeout(() => {
      if (this._consentCodeReceived) {
        this._consentCodeReceived = false;
        // It means that incrementStep has been executed in windowEventListener path.
        // In this way we avoid race conditions.
        return;
      }

      this.checkForNavigationToEntitiesProgress();
    }, 1500);
  }

  /**
   * ------------------------------------------------------------------------------------------------------
   * Entities State, new behaviour for mobile integration via webivew.
   * ------------------------------------------------------------------------------------------------------
   */

  /**
   * Check for state received.
   */
  checkForQueryParamsState(): void {
    const queryParamsEntitiesState = sessionStorage.getItem('entitiesState');

    if (!queryParamsEntitiesState) {
      return;
    }
    this.entitiesState = this.parseBase64EntitiesState(
      queryParamsEntitiesState,
    );

    !!this.entitiesState && this.setEntitiesState(this.entitiesState);
  }

  /**
   * Update entities state. Only applies to mobile. Not valid in web.
   * @param currentEntityCode
   */
  updateEntitiesState(): void {
    this.entitiesState = {
      index: this.index,
      step: this.step,
      entitiesLength:
        JSON.parse(sessionStorage.getItem('selectedEntities')!)?.length ?? 0,
      entities: JSON.parse(sessionStorage.getItem('selectedEntities')!) ?? [],
      retry: sessionStorage.getItem('retry'),
      retryId: sessionStorage.getItem('retryId'),
      retryIndex: sessionStorage.getItem('retryIndex'),
      currentAggregatedEntities:
        JSON.parse(sessionStorage.getItem('currentAggregatedEntities')!) ??
        undefined,
    };
  }

  /**
   * Set state into current variables. Only applies to mobile. Not valid in web.
   * @param state
   */
  setEntitiesState(state: IEntitiesState): void {
    this.index = state.index;
    this.step = state.step;
    this.entitiesLength = state.entitiesLength;
    this.entities = state.entities;

    !!state.retry && sessionStorage.setItem('retry', state.retry);
    !!state.retryId && sessionStorage.setItem('retryId', state.retryId);
    !!state.currentAggregatedEntities &&
      sessionStorage.setItem(
        'currentAggregatedEntities',
        JSON.stringify(state.currentAggregatedEntities),
      );
  }

  /**
   * Checks if there are entities left to consent, else navigate to entities-progress.
   * @returns
   */
  checkForNavigationToEntitiesProgress(): void {
    let retry = JSON.parse(sessionStorage.getItem('retry')!);
    if (this.entitiesLength - 1 > this.index && !retry) {
      this.index++;
      this.step = 1;
      this.incrementStep();
      return;
    }
    this._router.navigate(['/entities-progress']);
  }

  /**
   * Get entity state in base64 string. Only applies to mobile. Not valid in web.
   * @param currentEntityCode
   * @returns string Base64 entity state.
   */
  getBASE64EntitiesState(): string {
    return btoa(JSON.stringify(this.entitiesState));
  }

  /**
   * Parse received base64 entities state. Only applies to mobile. Not valid in web.
   * @param base64EntitiesState
   * @returns IEntitiesState current state.
   */
  parseBase64EntitiesState(base64EntitiesState: string): IEntitiesState {
    return JSON.parse(atob(base64EntitiesState));
  }

  /**
   * Check wether is webview or not.
   */
  checkIsWebview(): void {
    this.isWebview = JSON.parse(sessionStorage.getItem('iswebview') ?? 'false');
  }

  /**
   * If there's a selectedEntities item in Session Storage, means that mobile flow has entered entities list
   * view and those entities should be used.
   * Else we need to update that item with current state entities.
   *
   * Only applies to mobile. Not valid in web.
   */
  checkForSelectedEntities(): void {
    // Entities state is currently loaded into this.entitiesState, so we don't want this
    // behaviour in session storage.
    sessionStorage.removeItem('entitiesState');

    let selectedEntities: any = sessionStorage.getItem('selectedEntities');
    if (!selectedEntities) {
      this.setSelectedEntities(this.entitiesState!.entities);
      return;
    }

    selectedEntities = JSON.parse(selectedEntities);

    // Merge consentCode from entitiesState into selectedEntities.
    // When sessionStorage is preserved during webview cross-domain navigation,
    // selectedEntities lacks the consentCode that was set in entitiesState
    // by the messages component after returning from bank consent.
    if (this.entitiesState?.entities) {
      let merged = false;
      selectedEntities.forEach((entity: EntityInterface) => {
        const stateEntity = this.entitiesState!.entities.find(
          (e: EntityInterface) => e.id === entity.id,
        );
        if (stateEntity?.consentCode && !entity.consentCode) {
          entity.consentCode = stateEntity.consentCode;
          merged = true;
        }
      });

      if (merged) {
        // Re-populate EntityService with merged entities so that
        // entities-progress gets entities with consentCode
        this._entityService.removeSelectedEntities();
        selectedEntities.forEach((entity: EntityInterface) => {
          this._entityService.setSelectedEntities(entity);
          this._entityService.setSelectedEntityProductType(
            EntityProductType.psd2,
          );
        });
      }
    }

    this.entities = selectedEntities;
    this.entitiesLength = selectedEntities.length;
    this.updateEntitiesState();
  }

  /**
   * Set selected entities into entityService.  Only applies to mobile. Not valid in web.
   * It will set session storage item for every entity processed.
   * @param entities EntityInterface[]
   */
  setSelectedEntities(entities: EntityInterface[]): void {
    entities.forEach((entity) => {
      this._entityService.setSelectedEntities(entity);
      this._entityService.setSelectedEntityProductType(EntityProductType.psd2);
    });
  }

  /**
   * Check if it's a retry flow.
   */
  isRetry(): void {
    let retry = JSON.parse(sessionStorage.getItem('retry')!);

    if (retry) {
      let id = JSON.parse(sessionStorage.getItem('retryId')!);
      this.entities = this.entities.filter((entity) => entity.id === id);
    }
  }

  addOrUpdateEntitiesStateParam(
    aggregationRequestParams: any[] | undefined,
  ): void {
    if (!aggregationRequestParams) {
      return;
    }

    let paramToUpdate = aggregationRequestParams.find(
      (param: any) => param.name === 'entitiesState',
    );
    if (paramToUpdate) {
      paramToUpdate.value = this.getBASE64EntitiesState();
      return;
    }

    aggregationRequestParams.push({
      name: 'entitiesState',
      value: this.getBASE64EntitiesState(),
    });
  }
}
